/**
 * The blocked days and times, kept current while the customer is choosing.
 *
 * Admins block days and half-hour slots in the dashboard at any moment. This
 * step used to read them once through urql's cache-first default, so a slot
 * blocked after the customer opened the step stayed bookable in their tab
 * (17 Sep feedback). Now it re-asks on a timer and whenever the tab comes back
 * into view, as `useSameDayAvailability` does, and reports what *newly* became
 * blocked so the step can tell the customer.
 *
 * The server refuses a blocked slot at checkout either way
 * (`apps/api/src/lib/schedule-guard.ts`). This is what keeps the customer from
 * getting that far on a stale screen.
 *
 * Queries go through the client rather than `useQuery` so every answer carries
 * the date it was asked for. urql keeps the previous date's data on screen
 * while the next one loads, and diffing that against the new date's answer
 * would announce blocks that are only a different day's.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useClient } from "urql";
import {
  GET_BOOKED_DATES,
  GET_BOOKED_TIMES,
} from "../graphql/queries/schedule";

/** Often enough that a customer reading the grid sees a block within a minute. */
export const SCHEDULE_POLL_MS = 30_000;

export interface ScheduleChanges {
  /** Days that became fully booked since the last answer. */
  onDatesBlocked?: (added: string[]) => void;
  /** Times ("1030") on `date` that became blocked since the last answer. */
  onTimesBlocked?: (date: string, added: string[]) => void;
}

export function useLiveSchedule(
  cleanDate: string,
  changes: ScheduleChanges = {}
) {
  const client = useClient();
  const handlers = useRef(changes);
  handlers.current = changes;

  const [dates, setDates] = useState<string[] | undefined>();
  /** The first answer for the days never came. The step opens anyway, as before. */
  const [datesFailed, setDatesFailed] = useState(false);
  const [times, setTimes] = useState<
    { date: string; times: string[] } | undefined
  >();
  /** A first answer for this date that never came. Shown as open, as before. */
  const [timesFailedFor, setTimesFailedFor] = useState<string | undefined>();

  const datesSeen = useRef<string[]>();
  const timesSeen = useRef<{ date: string; times: string[] }>();
  const currentDate = useRef(cleanDate);
  currentDate.current = cleanDate;

  const loadDates = useCallback(async () => {
    const result = await client
      .query(GET_BOOKED_DATES, {}, { requestPolicy: "network-only" })
      .toPromise();
    const next: string[] | undefined = result.data?.bookedDates;
    // A failed poll has learned nothing; keep the last answer.
    if (!next) {
      if (!datesSeen.current) setDatesFailed(true);
      return;
    }

    const previous = datesSeen.current;
    datesSeen.current = next;
    setDates(next);

    const added = previous ? next.filter((d) => !previous.includes(d)) : [];
    if (added.length) handlers.current.onDatesBlocked?.(added);
  }, [client]);

  const loadTimes = useCallback(
    async (date: string) => {
      const result = await client
        .query(GET_BOOKED_TIMES, { date }, { requestPolicy: "network-only" })
        .toPromise();
      // The customer picked another day while this was in flight.
      if (currentDate.current !== date) return;

      const next: string[] | undefined = result.data?.bookedTimes;
      if (!next) {
        if (timesSeen.current?.date !== date) setTimesFailedFor(date);
        return;
      }

      const previous = timesSeen.current;
      timesSeen.current = { date, times: next };
      setTimes({ date, times: next });

      // Only a change on the same day is news. The first answer for a day is not.
      if (previous?.date !== date) return;
      const added = next.filter((t) => !previous.times.includes(t));
      if (added.length) handlers.current.onTimesBlocked?.(date, added);
    },
    [client]
  );

  useEffect(() => {
    loadDates();
  }, [loadDates]);

  useEffect(() => {
    if (cleanDate) loadTimes(cleanDate);
  }, [cleanDate, loadTimes]);

  useEffect(() => {
    const refresh = () => {
      loadDates();
      if (currentDate.current) loadTimes(currentDate.current);
    };
    const timer = setInterval(refresh, SCHEDULE_POLL_MS);
    // A backgrounded tab throttles its timers; re-ask the moment it is back.
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [loadDates, loadTimes]);

  const timesAreForDate = times?.date === cleanDate;

  return {
    bookedDates: dates,
    loadingBookedDates: dates === undefined && !datesFailed,
    bookedTimes: timesAreForDate ? times!.times : undefined,
    loadingBookedTimes:
      Boolean(cleanDate) && !timesAreForDate && timesFailedFor !== cleanDate,
  };
}
